import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  Volunteer,
  VolunteerListItem,
} from '../interfaces/volunteer.interfaces';
import {
  BackendVolunteer,
  mapVolunteer,
  mapVolunteerToListItem,
} from '../mappers/volunteer.mapper';
import { environment } from '../../environments/environment';

interface VolunteerListResponse {
  status: string;
  results: number;
  data: {
    documents: BackendVolunteer[];
  };
}

interface VolunteerResponse {
  status: string;
  data: {
    document: BackendVolunteer;
  };
}

// ---------------------------------------------------------------------------
// Fase D.1 — Historial de voluntarios (rutas admin en backend B3)
//   GET /volunteers/history/students   -> con horasAcreditadas
//   GET /volunteers/history/volunteers -> SIN horasAcreditadas
//   GET /volunteers/history/audit      -> bitácora de transiciones de estado
// Tipados laxos a propósito (las formas exactas las dicta el backend).
// ---------------------------------------------------------------------------

export interface PastStudentHistoryItem {
  _id: string;
  folio?: string;
  nombreCompleto?: string;
  email?: string;
  accountStatus?: string;
  fechaDesactivacion?: string;
  horasAcreditadas?: number;
  [key: string]: any;
}

export interface PastVolunteerHistoryItem {
  _id: string;
  folio?: string;
  nombreCompleto?: string;
  email?: string;
  accountStatus?: string;
  fechaDesactivacion?: string;
  [key: string]: any;
}

export interface StatusAuditItem {
  role?: string;
  folio?: string;
  nombre?: string;
  fromStatus?: string;
  toStatus?: string;
  reason?: string;
  comments?: string;
  changedByEmail?: string;
  date?: string;
  [key: string]: any;
}

interface HistoryListResponse<T> {
  status: string;
  results: number;
  data: {
    documents: T[];
  };
}

@Injectable({
  providedIn: 'root',
})
export class VolunteerService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/volunteer`;

  createVolunteer(data: Partial<BackendVolunteer>): Observable<VolunteerResponse> {
    return this.http.post<VolunteerResponse>(
      this.baseUrl,
      data,
    );
  }

  getVolunteers(): Observable<VolunteerListItem[]> {
    return this.http
      .get<VolunteerListResponse>(this.baseUrl)
      .pipe(
        map((response) =>
          response.data.documents.map((volunteer) =>
            mapVolunteerToListItem(mapVolunteer(volunteer)),
          ),
        ),
      );
  }

  getAllVolunteers(): Observable<Volunteer[]> {
    return this.http
      .get<VolunteerListResponse>(this.baseUrl)
      .pipe(
        map((response) =>
          response.data.documents.map(
            mapVolunteer,
          ),
        ),
      );
  }

  /**
   * Fase D.3 (Módulo 1 - RA1-1): marca primerIngresoRealizado=true tras el
   * primer ingreso del voluntario ya activo.
   *   PATCH {apiUrl}/volunteer/:id/primer-ingreso
   */
  markPrimerIngreso(id: string): Observable<Volunteer> {
    return this.http
      .patch<VolunteerResponse>(
        `${this.baseUrl}/${id}/primer-ingreso`,
        {},
      )
      .pipe(map((response) => mapVolunteer(response.data.document)));
  }

  getVolunteer(id: string): Observable<Volunteer> {
    return this.http
      .get<VolunteerResponse>(`${this.baseUrl}/${id}`)
      .pipe(
        map((response) =>
          mapVolunteer(response.data.document),
        ),
      );
  }

  getVolunteerByAuthId(authId: string): Observable<Volunteer> {
    return this.http
      .get<VolunteerResponse>(`${this.baseUrl}/auth/${authId}`)
      .pipe(map((response) => mapVolunteer(response.data.document)));
  }

  updateVolunteer(id: string, data: Partial<Volunteer> | any): Observable<Volunteer> {
    return this.http
      .patch<VolunteerResponse>(`${this.baseUrl}/${id}`, data)
      .pipe(map((response) => mapVolunteer(response.data.document)));
  }

  updateVolunteerAccountStatus(
    id: string,
    accountStatus: 'pending' | 'active' | 'inactive' | 'rejected',
    reason?: string,
    comments?: string,
  ): Observable<Volunteer> {
    return this.http
      .patch<VolunteerResponse>(
        `${this.baseUrl}/${id}/account-status`,
        { accountStatus, reason, comments },
      )
      .pipe(
        map((response) =>
          mapVolunteer(response.data.document),
        ),
      );
  }

  getMyProfile(): Observable<Volunteer> {
    return this.http
      .get<VolunteerResponse>(
        `${this.baseUrl}/me`,
      )
      .pipe(
        map((response) =>
          mapVolunteer(response.data.document),
        ),
      );
  }

  updateMyProfile(data: {
    correoPersonal?: string;
    telefono?: string;
    bioPersonal?: string;
    facebookUsuario?: string;
    instagramUsuario?: string;
    tiktokUsuario?: string;
    xUsuario?: string;
    youtubeUsuario?: string;
    linkedinUsuario?: string;
  }): Observable<Volunteer> {
    return this.http
      .patch<VolunteerResponse>(
        `${this.baseUrl}/me`,
        data,
      )
      .pipe(
        map((response) =>
          mapVolunteer(response.data.document),
        ),
      );
  }

  updateVolunteerStatus(
    id: string,
    status: 'pending' | 'active' | 'inactive' | 'rejected',
  ): Observable<Volunteer> {
    return this.http
      .patch<VolunteerResponse>(`${this.baseUrl}/${id}/status`, { status })
      .pipe(map((response) => mapVolunteer(response.data.document)));
  }

  acceptVolunteer(id: string, comments?: string): Observable<Volunteer> {
    return this.updateVolunteerAccountStatus(id, 'active', 'admin_approval', comments);
  }

  rejectVolunteer(id: string, comments?: string): Observable<Volunteer> {
    return this.updateVolunteerAccountStatus(id, 'rejected', 'admin_rejection', comments);
  }

  // -------------------------------------------------------------------------
  // Fase D.1 — Historial (B3). El router de volunteer está montado en
  // /volunteer (SINGULAR) en app.js, y las rutas history viven dentro de él
  // como /history/*. Verificado por curl: /volunteer/history/students -> 200.
  // -------------------------------------------------------------------------
  private readonly historyUrl = `${this.baseUrl}/history`;

  getPastStudents(): Observable<PastStudentHistoryItem[]> {
    return this.http
      .get<HistoryListResponse<PastStudentHistoryItem>>(
        `${this.historyUrl}/students`,
      )
      .pipe(map((response) => response?.data?.documents ?? []));
  }

  getPastVolunteers(): Observable<PastVolunteerHistoryItem[]> {
    return this.http
      .get<HistoryListResponse<PastVolunteerHistoryItem>>(
        `${this.historyUrl}/volunteers`,
      )
      .pipe(map((response) => response?.data?.documents ?? []));
  }

  getStatusAudit(): Observable<StatusAuditItem[]> {
    return this.http
      .get<HistoryListResponse<StatusAuditItem>>(
        `${this.historyUrl}/audit`,
      )
      .pipe(map((response) => response?.data?.documents ?? []));
  }
}
